import { useCallback, useState } from "react";
import { toast } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useCreateUserMutation } from "~/graphql/mainHooks";
import { useFetchCanopyRolesData } from "~/pages/UserAndRoles/CanopyRolesPage/hooks";
import { toDbId } from "~/utils/graphql-helpers";

export type FormValues = { firstName: string; lastName: string; email: string; role: string };
export type CanopyRoleData = { id: string; title: string; memberCount: number };

export const useCreateUserForm = (onUserCreated?: (userId: string) => void) => {
  const [showModal, setShowModal] = useState(false);
  const { rowData, useGetGroupsQueryResult } = useFetchCanopyRolesData();
  const [createUser, { loading: isSubmitting }] = useCreateUserMutation({
    context: withClientContext("staff"),
  });

  const onHide = useCallback(() => {
    setShowModal(false);
  }, []);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const onSubmit = useCallback(
    async (values: Partial<FormValues>) => {
      try {
        const groups = values.role ? [values.role] : undefined;

        const response = await createUser({
          variables: {
            emailAddress: (values.email || "").trim(),
            firstName: (values.firstName || "").trim(),
            lastName: (values.lastName || "").trim(),
            groups,
          },
          refetchQueries: ["GetCCToolsUsers"],
        });

        if (response.data?.userOps?.createUser?.user) {
          const createdUser = response.data.userOps.createUser.user;
          onHide();
          toast.success("User created successfully");

          if (onUserCreated && createdUser.id) {
            const userId = toDbId(createdUser.id).toString();
            onUserCreated(userId);
          }
        } else {
          toast.error("There was an error creating the user");
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        toast.error(`Failed to create user: ${errorMessage}`);
      }
    },
    [createUser, onHide, onUserCreated],
  );

  const initialValues: FormValues = { firstName: "", lastName: "", email: "", role: "" };

  return {
    initialValues,
    onHide,
    openModal,
    showModal,
    onSubmit,
    isSubmitting,
    roleOptions: rowData,
    useGetGroupsQueryResult,
  };
};
